import { CompanyUser } from '@/api';
import { useMemo } from 'react';
import { useCompaniesTranslation } from '../hooks';

type UseViewAppDevicesActionProps = {
    user: CompanyUser;
};

type UseViewAppDevicesActionData = {
    /** Whether this viewer can see this member's app devices. */
    isAllowed: boolean;
    /** Why not. Empty while allowed. */
    reason: string;
};

/**
 * Seeing a member's app devices. Stricter than seeing the member at all — a
 * plain member of a team they share can read the member but not their devices.
 *
 * Like `useEditProfileAction` this carries no call of its own; it decides
 * whether the App Devices tab has anything to show.
 */
export function useViewAppDevicesAction(
    props: UseViewAppDevicesActionProps
): UseViewAppDevicesActionData {
    const { user } = props;

    const { t } = useCompaniesTranslation();

    const canViewAppDevices = user.capabilities.canViewAppDevices;
    return useMemo(() => {
        if (!canViewAppDevices.allowed) {
            return {
                isAllowed: false,
                reason:
                    canViewAppDevices.reason ??
                    t('actions.view_app_devices.reason_not_permitted')
            };
        }

        return { isAllowed: true, reason: '' };
    }, [canViewAppDevices, t]);
}
